import html2canvas from 'html2canvas'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Download, ImageDown, Pencil, Plus, Save, Trash2, Vote } from 'lucide-react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import SystemNoticeRichEditor from '../components/SystemNoticeRichEditor'
import { api, getApiErrorMessage, resolveApiAssetUrl } from '../lib/api'
import { asArray } from '../lib/collections'
import { formatMenuInstant } from '../lib/date'
import { RoleLabels, Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import SanitizedRichText from '../ui/SanitizedRichText'

function unwrapData(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res?.data
}

const STATUS_OPTIONS = [
  { id: 'ALL', label: '전체' },
  { id: 'ONGOING', label: '진행중' },
  { id: 'UPCOMING', label: '예정' },
  { id: 'ENDED', label: '종료' },
]

const VISIBILITY_OPTIONS = [
  { id: 'ALL', label: '전체' },
  { id: 'DRAFT', label: '숨김' },
  { id: 'TEST', label: '테스트' },
  { id: 'OPEN', label: '공개' },
  { id: 'ARCHIVED', label: '보관' },
]

const STATUS_LABELS = {
  UPCOMING: '예정',
  ONGOING: '진행중',
  ENDED: '종료',
}

const STATUS_BADGE_CLASS = {
  UPCOMING: 'bg-sky-50 border-sky-200 text-sky-700',
  ONGOING: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  ENDED: 'bg-slate-50 border-slate-200 text-slate-600',
}

const RESULT_POLICY_OPTIONS = [
  { id: 'AFTER_VOTE', label: '참여 후 공개' },
  { id: 'AFTER_END', label: '종료 후 공개' },
  { id: 'ALWAYS', label: '항상 공개' },
]

const COMMENT_MAX_LENGTH = 500

const TARGET_ORG_TYPE_OPTIONS = [
  { id: '', label: '전체' },
  { id: 'GA', label: 'GA' },
  { id: 'INSURER', label: '원수사' },
  { id: 'PERSONAL', label: '개인' },
]

function statusLabel(status) {
  return STATUS_LABELS[status] || '진행중'
}

function statusBadgeClass(status) {
  return STATUS_BADGE_CLASS[status] || STATUS_BADGE_CLASS.ONGOING
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(status)}`}>
      {statusLabel(status)}
    </span>
  )
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function toDatetimeLocalValue(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

function defaultStartValue() {
  return toDatetimeLocalValue(new Date())
}

function defaultEndValue() {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return toDatetimeLocalValue(d)
}

function isoFromDatetimeLocal(value) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

function htmlHasText(html) {
  const source = String(html || '')
  if (/<img\b/i.test(source)) return true
  return source.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim().length > 0
}

function formatFileSize(size) {
  const n = Number(size)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n < 1024) return `${n.toLocaleString('ko-KR')} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function roleLabel(role) {
  return RoleLabels[role] || role
}

export default function VoteBoardPage() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isOperator = useMemo(() => {
    const roles = [
      ...(Array.isArray(user?.roles) ? user.roles : []),
      ...(Array.isArray(user?.effectiveRoles) ? user.effectiveRoles : []),
      ...(Array.isArray(user?.accountRoles) ? user.accountRoles : []),
    ]
    return roles.includes(Roles.OPERATOR)
  }, [user])
  const isNew = location.pathname === '/vote-board/new'
  const isEdit = Boolean(id) && location.pathname === `/vote-board/${id}/edit`
  const detailId = id && !isNew && !isEdit ? id : null

  if (isNew) {
    return <VoteEditView isOperator={isOperator} navigate={navigate} />
  }
  if (isEdit) {
    return <VoteEditView isOperator={isOperator} navigate={navigate} editId={id} />
  }
  if (detailId) {
    return <VoteDetailView id={detailId} isOperator={isOperator} />
  }
  return <VoteListView isOperator={isOperator} />
}

function VoteListView({ isOperator }) {
  const [items, setItems] = useState([])
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [visibilityFilter, setVisibilityFilter] = useState('ALL')
  const [sortMode, setSortMode] = useState('LATEST')
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setErr('')
      try {
        const params = {}
        if (statusFilter !== 'ALL') params.status = statusFilter
        if (isOperator && visibilityFilter !== 'ALL') params.visibility = visibilityFilter
        if (sortMode === 'POPULAR') params.sort = 'POPULAR'
        if (q.trim()) params.q = q.trim()
        const res = await api.get('/vote-board', { params })
        const payload = unwrapData(res)
        const list = Array.isArray(payload?.items) ? payload.items : []
        if (!ignore) setItems(list)
      } catch (e) {
        console.error(e)
        if (!ignore) setErr(getApiErrorMessage(e, '투표 목록을 불러오지 못했습니다.'))
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    const timer = setTimeout(() => { void load() }, q ? 250 : 0)
    return () => { ignore = true; clearTimeout(timer) }
  }, [statusFilter, visibilityFilter, sortMode, q, isOperator])

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">투표게시판</h1>
        </div>
        {isOperator && (
          <Link to="/vote-board/new" className="inline-flex items-center gap-2 rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
            <Plus className="h-4 w-4" aria-hidden="true" />
            투표 작성
          </Link>
        )}
      </div>

      <div className="space-y-3 border-b pb-3">
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`rounded-full border px-3 py-1.5 text-sm font-medium ${statusFilter === option.id ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              onClick={() => setStatusFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            className={`rounded-full border px-3 py-1.5 text-sm font-medium ${sortMode === 'POPULAR' ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setSortMode((prev) => (prev === 'POPULAR' ? 'LATEST' : 'POPULAR'))}
          >
            인기
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="w-full rounded border px-3 py-2 text-sm sm:w-72"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="제목 또는 태그 검색"
          />
          {isOperator && (
            <select
              className="rounded border px-3 py-2 text-sm"
              value={visibilityFilter}
              onChange={(event) => setVisibilityFilter(event.target.value)}
            >
              {VISIBILITY_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          )}
        </div>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      <div className="rounded border">
        {loading ? (
          <div className="p-6 text-sm text-slate-600">불러오는 중...</div>
        ) : items.length > 0 ? (
          <div className="divide-y">
            {items.map((item) => (
              <Link key={item.id} to={`/vote-board/${item.id}`} className="block px-4 py-4 hover:bg-slate-50">
                <div className="flex flex-wrap items-center gap-2">
                  {item.notice && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">공지</span>}
                  <StatusBadge status={item.status} />
                  {item.visibilityLabel && <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">{item.visibilityLabel}</span>}
                  {item.hasVoted && <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">참여완료</span>}
                  <div className="min-w-0 flex-1 truncate font-semibold text-slate-900">{item.title}</div>
                  <div className="text-xs text-slate-500">{formatMenuInstant(item, 'createdAt')}</div>
                </div>
                <div className="mt-2 line-clamp-2 text-sm text-slate-600">{item.contentPreview}</div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span>{formatMenuInstant(item, 'voteStartAt')} ~ {formatMenuInstant(item, 'voteEndAt')}</span>
                  <span>참여 {Number(item.voteCount || 0).toLocaleString('ko-KR')}</span>
                  <span>댓글 {Number(item.commentCount || 0).toLocaleString('ko-KR')}</span>
                  <span>{item.targetSummary || '전체 대상'}</span>
                  {(item.tags || []).map((tag) => <span key={tag} className="rounded bg-slate-100 px-1.5 py-0.5">#{tag}</span>)}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-slate-500">등록된 투표가 없습니다.</div>
        )}
      </div>
    </section>
  )
}

function VoteEditView({ isOperator, navigate, editId = null }) {
  const [form, setForm] = useState(() => ({
    title: '',
    content: '',
    notice: false,
    allowMultiple: false,
    maxChoices: 2,
    allowVoteChange: false,
    allowComments: true,
    resultPolicy: 'AFTER_VOTE',
    visibility: 'TEST',
    targetOrgType: '',
    tags: '',
    sponsorName: '',
    resultSummary: '',
    voteStartAt: defaultStartValue(),
    voteEndAt: defaultEndValue(),
    options: [{ label: '' }, { label: '' }],
    targetOrgs: [],
    targetRoles: [],
  }))
  const [targetOptions, setTargetOptions] = useState({ orgs: [], roles: [] })
  const [orgSearch, setOrgSearch] = useState('')
  const [attachmentFiles, setAttachmentFiles] = useState([])
  const [existingAttachments, setExistingAttachments] = useState([])
  const [saving, setSaving] = useState(false)
  const [initialLoading, setInitialLoading] = useState(false)
  const [err, setErr] = useState('')
  const isEdit = Boolean(editId)

  useEffect(() => {
    if (!isOperator) return undefined
    let ignore = false
    async function loadTargets() {
      try {
        const params = orgSearch.trim() ? { q: orgSearch.trim(), size: 120 } : { size: 120 }
        const res = await api.get('/vote-board/admin/target-options', { params })
        const payload = unwrapData(res)
        if (!ignore) setTargetOptions({ orgs: payload?.orgs || [], roles: payload?.roles || [] })
      } catch {
        if (!ignore) setTargetOptions({ orgs: [], roles: [] })
      }
    }
    const timer = setTimeout(() => { void loadTargets() }, orgSearch ? 250 : 0)
    return () => { ignore = true; clearTimeout(timer) }
  }, [isOperator, orgSearch])

  useEffect(() => {
    if (!editId) return undefined
    let ignore = false
    async function loadForEdit() {
      setInitialLoading(true)
      setErr('')
      try {
        const res = await api.get(`/vote-board/${editId}`)
        const payload = unwrapData(res)
        if (ignore) return
        setForm({
          title: payload?.title || '',
          content: payload?.content || '',
          notice: Boolean(payload?.notice),
          allowMultiple: Boolean(payload?.allowMultiple),
          maxChoices: Number(payload?.maxChoices || 2),
          allowVoteChange: Boolean(payload?.allowVoteChange),
          allowComments: payload?.allowComments !== false,
          resultPolicy: payload?.resultPolicy || 'AFTER_VOTE',
          visibility: payload?.visibility || 'TEST',
          targetOrgType: payload?.targetOrgType || '',
          tags: Array.isArray(payload?.tags) ? payload.tags.join(', ') : '',
          sponsorName: payload?.sponsorName || '',
          resultSummary: payload?.resultSummary || '',
          voteStartAt: toDatetimeLocalValue(payload?.voteStartAt || new Date()),
          voteEndAt: toDatetimeLocalValue(payload?.voteEndAt || new Date()),
          options: (asArray(payload?.options)).map((option) => ({
            id: option.id,
            label: option.label || '',
            description: option.description || '',
          })).filter((option) => option.label || option.id).concat([{ label: '' }]).slice(0, 20),
          targetOrgs: (asArray(payload?.targetOrgs)).map((row) => ({
            orgId: row.orgId,
            includeDescendants: row.includeDescendants !== false,
          })),
          targetRoles: Array.isArray(payload?.targetRoles) ? payload.targetRoles : [],
        })
        setExistingAttachments(Array.isArray(payload?.attachments) ? payload.attachments : [])
      } catch (e) {
        console.error(e)
        if (!ignore) setErr(getApiErrorMessage(e, '투표 내용을 불러오지 못했습니다.'))
      } finally {
        if (!ignore) setInitialLoading(false)
      }
    }
    void loadForEdit()
    return () => { ignore = true }
  }, [editId])

  if (!isOperator) {
    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">투표 작성</h1>
          <Link to="/vote-board" className="rounded border px-3 py-2 text-sm hover:bg-slate-50">목록</Link>
        </div>
        <div className="rounded border p-6 text-sm text-slate-600">운영자만 투표를 작성할 수 있습니다.</div>
      </section>
    )
  }

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }))
  const updateOption = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((option, i) => (i === index ? { ...option, ...patch } : option)),
    }))
  }
  const removeOption = (index) => {
    setForm((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }))
  }
  const addOption = () => {
    setForm((prev) => ({ ...prev, options: [...prev.options, { label: '', description: '' }].slice(0, 20) }))
  }
  const onAttachmentChange = (event) => {
    const files = Array.from(event.target.files || [])
      .filter((file) => String(file?.type || '').startsWith('image/'))
      .slice(0, 10)
    setAttachmentFiles(files)
  }
  const removeExistingAttachment = (fileId) => {
    setExistingAttachments((prev) => prev.filter((file) => file.fileId !== fileId))
  }
  const removeAttachment = (index) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index))
  }
  const setTargetOrgIds = (values) => {
    update({ targetOrgs: values.map((value) => ({ orgId: Number(value), includeDescendants: true })).filter((row) => row.orgId > 0) })
  }
  const setTargetRoles = (values) => {
    update({ targetRoles: values })
  }
  const uploadAttachments = async () => {
    const uploaded = existingAttachments.map((file) => file.fileId).filter(Boolean)
    for (const file of attachmentFiles) {
      const body = new FormData()
      body.append('file', file)
      const res = await api.post('/files/upload/vote-board-image', body, { headers: { 'Content-Type': 'multipart/form-data' } })
      const payload = unwrapData(res)
      if (payload?.fileId) uploaded.push(payload.fileId)
    }
    return uploaded
  }

  const onSave = async (event) => {
    event.preventDefault()
    const cleanOptions = form.options
      .map((option) => ({ id: option.id || null, label: String(option.label || '').trim(), description: String(option.description || '').trim() }))
      .filter((option) => option.label)
    if (!form.title.trim() || !htmlHasText(form.content) || cleanOptions.length < 2) {
      setErr('제목, 내용, 선택지 2개 이상을 입력해 주세요.')
      return
    }
    const voteStartAt = isoFromDatetimeLocal(form.voteStartAt)
    const voteEndAt = isoFromDatetimeLocal(form.voteEndAt)
    if (!voteStartAt || !voteEndAt || new Date(voteEndAt).getTime() <= new Date(voteStartAt).getTime()) {
      setErr('투표 기간을 다시 확인해 주세요.')
      return
    }
    try {
      setSaving(true)
      setErr('')
      const attachmentFileIds = await uploadAttachments()
      const payload = {
        ...form,
        title: form.title.trim(),
        content: form.content,
        maxChoices: form.allowMultiple ? Number(form.maxChoices || 2) : 1,
        targetOrgType: form.targetOrgType || null,
        targetOrgs: form.targetOrgs,
        targetRoles: form.targetRoles,
        voteStartAt,
        voteEndAt,
        options: cleanOptions,
        attachmentFileIds,
      }
      const res = isEdit ? await api.patch(`/vote-board/${editId}`, payload) : await api.post('/vote-board', payload)
      const result = unwrapData(res)
      navigate(isEdit ? `/vote-board/${editId}` : (result?.id ? `/vote-board/${result.id}` : '/vote-board'))
    } catch (e) {
      console.error(e)
      setErr(getApiErrorMessage(e, isEdit ? '투표 수정에 실패했습니다.' : '투표 등록에 실패했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  if (initialLoading) {
    return <div className="text-sm text-slate-600">불러오는 중...</div>
  }

  const selectedOrgIds = form.targetOrgs.map((row) => String(row.orgId))
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">{isEdit ? '투표 수정' : '투표 작성'}</h1>
        <Link to={isEdit ? `/vote-board/${editId}` : '/vote-board'} className="rounded border px-3 py-2 text-sm hover:bg-slate-50">목록</Link>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      <form onSubmit={onSave} className="space-y-4 rounded border p-4">
        <div className="grid gap-3 lg:grid-cols-3">
          <div className="space-y-1 lg:col-span-2">
            <label className="text-sm text-slate-700" htmlFor="vote-title">제목</label>
            <input id="vote-title" className="w-full rounded border px-3 py-2" value={form.title} onChange={(e) => update({ title: e.target.value })} disabled={saving} required />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="vote-visibility">공개 상태</label>
            <select id="vote-visibility" className="w-full rounded border px-3 py-2" value={form.visibility} onChange={(e) => update({ visibility: e.target.value })} disabled={saving}>
              {VISIBILITY_OPTIONS.filter((o) => o.id !== 'ALL').map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="vote-start-at">시작일</label>
            <input id="vote-start-at" type="datetime-local" className="w-full rounded border px-3 py-2" value={form.voteStartAt} onChange={(e) => update({ voteStartAt: e.target.value })} disabled={saving} required />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="vote-end-at">종료일</label>
            <input id="vote-end-at" type="datetime-local" className="w-full rounded border px-3 py-2" value={form.voteEndAt} onChange={(e) => update({ voteEndAt: e.target.value })} disabled={saving} required />
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.notice} onChange={(e) => update({ notice: e.target.checked })} disabled={saving} />
            공지로 상단 고정
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.allowVoteChange} onChange={(e) => update({ allowVoteChange: e.target.checked })} disabled={saving} />
            참여 후 선택 변경 허용
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.allowComments} onChange={(e) => update({ allowComments: e.target.checked })} disabled={saving} />
            댓글 허용
          </label>
        </div>

        <div className="grid gap-3 lg:grid-cols-3">
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="vote-result-policy">결과 공개</label>
            <select id="vote-result-policy" className="w-full rounded border px-3 py-2" value={form.resultPolicy} onChange={(e) => update({ resultPolicy: e.target.value })} disabled={saving}>
              {RESULT_POLICY_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 self-end text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.allowMultiple} onChange={(e) => update({ allowMultiple: e.target.checked })} disabled={saving} />
            복수 선택
          </label>
          {form.allowMultiple && (
            <div className="space-y-1">
              <label className="text-sm text-slate-700" htmlFor="vote-max-choices">최대 선택 수</label>
              <input id="vote-max-choices" type="number" min="2" max="20" className="w-full rounded border px-3 py-2" value={form.maxChoices} onChange={(e) => update({ maxChoices: e.target.value })} disabled={saving} />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm text-slate-700">선택지</label>
          <div className="space-y-2">
            {form.options.map((option, index) => (
              <div key={`${option.id || 'new'}-${index}`} className="grid gap-2 rounded border bg-slate-50 p-3 md:grid-cols-[1fr_1fr_auto]">
                <input className="rounded border px-3 py-2 text-sm" value={option.label || ''} onChange={(e) => updateOption(index, { label: e.target.value })} placeholder={`선택지 ${index + 1}`} disabled={saving} />
                <input className="rounded border px-3 py-2 text-sm" value={option.description || ''} onChange={(e) => updateOption(index, { description: e.target.value })} placeholder="설명" disabled={saving} />
                <button type="button" className="inline-flex items-center justify-center rounded border bg-white px-3 py-2 text-sm hover:bg-slate-100 disabled:opacity-50" onClick={() => removeOption(index)} disabled={saving || form.options.length <= 2}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={addOption} disabled={saving || form.options.length >= 20}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            선택지 추가
          </button>
        </div>

        <div className="space-y-1">
          <label className="text-sm text-slate-700">내용</label>
          <SystemNoticeRichEditor value={form.content} onChange={(content) => update({ content })} uploadPath="/files/upload/vote-board-image" placeholder="투표 내용을 입력하세요." testId="vote-body-editor" />
        </div>

        <div className="grid gap-3 lg:grid-cols-3">
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="vote-tags">태그</label>
            <input id="vote-tags" className="w-full rounded border px-3 py-2" value={form.tags} onChange={(e) => update({ tags: e.target.value })} placeholder="보험사전산, 보상" disabled={saving} />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="vote-sponsor">의뢰/스폰서</label>
            <input id="vote-sponsor" className="w-full rounded border px-3 py-2" value={form.sponsorName} onChange={(e) => update({ sponsorName: e.target.value })} disabled={saving} />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="vote-target-type">대상 유형</label>
            <select id="vote-target-type" className="w-full rounded border px-3 py-2" value={form.targetOrgType} onChange={(e) => update({ targetOrgType: e.target.value })} disabled={saving}>
              {TARGET_ORG_TYPE_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm text-slate-700" htmlFor="vote-org-search">대상 조직</label>
            <input id="vote-org-search" className="w-full rounded border px-3 py-2 text-sm" value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} placeholder="조직명 검색" />
            <select multiple className="h-40 w-full rounded border px-3 py-2 text-sm" value={selectedOrgIds} onChange={(e) => setTargetOrgIds(Array.from(e.target.selectedOptions).map((option) => option.value))}>
              {(targetOptions.orgs || []).map((org) => (
                <option key={org.id} value={org.id}>{org.name} · {org.typeLabel}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm text-slate-700" htmlFor="vote-target-roles">대상 직군</label>
            <select id="vote-target-roles" multiple className="h-40 w-full rounded border px-3 py-2 text-sm" value={form.targetRoles} onChange={(e) => setTargetRoles(Array.from(e.target.selectedOptions).map((option) => option.value))}>
              {(targetOptions.roles || []).map((role) => (
                <option key={role.code} value={role.code}>{roleLabel(role.code)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm text-slate-700" htmlFor="vote-result-summary">종료 후 운영자 코멘트</label>
          <textarea id="vote-result-summary" className="min-h-[96px] w-full rounded border px-3 py-2" value={form.resultSummary} onChange={(e) => update({ resultSummary: e.target.value })} disabled={saving} />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-slate-700" htmlFor="vote-attachments">첨부 이미지</label>
          <input id="vote-attachments" type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" multiple className="block w-full rounded border px-3 py-2 text-sm" onChange={onAttachmentChange} disabled={saving} />
          {existingAttachments.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {existingAttachments.map((file) => (
                <div key={file.fileId || file.id} className="flex items-center justify-between gap-2 rounded border bg-slate-50 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{file.name || file.fileId}</div>
                    <div className="text-xs text-slate-500">{formatFileSize(file.size) || file.contentType || '-'}</div>
                  </div>
                  <button type="button" className="rounded border bg-white px-2 py-1 text-xs hover:bg-slate-100" onClick={() => removeExistingAttachment(file.fileId)} disabled={saving}>제거</button>
                </div>
              ))}
            </div>
          )}
          {attachmentFiles.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {attachmentFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-2 rounded border bg-slate-50 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{file.name}</div>
                    <div className="text-xs text-slate-500">{formatFileSize(file.size)}</div>
                  </div>
                  <button type="button" className="rounded border bg-white px-2 py-1 text-xs hover:bg-slate-100" onClick={() => removeAttachment(index)} disabled={saving}>제거</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button type="submit" className="inline-flex items-center gap-2 rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" disabled={saving}>
            <Save className="h-4 w-4" aria-hidden="true" />
            {saving ? '저장 중...' : '저장'}
          </button>
          <Link to={isEdit ? `/vote-board/${editId}` : '/vote-board'} className="rounded border px-4 py-2 text-sm hover:bg-slate-50">취소</Link>
        </div>
      </form>
    </section>
  )
}

function VoteDetailView({ id, isOperator }) {
  const navigate = useNavigate()
  const resultRef = useRef(null)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [selectedOptionIds, setSelectedOptionIds] = useState([])
  const [voting, setVoting] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [commentAnonymous, setCommentAnonymous] = useState(true)
  const [commentFilter, setCommentFilter] = useState('ALL')
  const [commenting, setCommenting] = useState(false)
  const [deletingPost, setDeletingPost] = useState(false)
  const [deletingCommentId, setDeletingCommentId] = useState(null)

  async function loadDetail() {
    setLoading(true)
    setErr('')
    try {
      const res = await api.get(`/vote-board/${id}`)
      const payload = unwrapData(res)
      setDetail(payload)
      const mine = Array.isArray(payload?.myVote?.options) ? payload.myVote.options.map((option) => option.optionId) : []
      setSelectedOptionIds(mine)
    } catch (e) {
      console.error(e)
      setErr(getApiErrorMessage(e, '투표 내용을 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const options = Array.isArray(detail?.options) ? detail.options.filter((option) => option.active !== false || detail?.canEdit) : []
  const comments = Array.isArray(detail?.comments) ? detail.comments : []
  const filteredComments = commentFilter === 'ALL' ? comments : comments.filter((comment) => String(comment.selectedOptionId || '') === String(commentFilter))
  const attachments = Array.isArray(detail?.attachments) ? detail.attachments : []
  const myVoteOptions = Array.isArray(detail?.myVote?.options) ? detail.myVote.options : []
  const myVoteOptionLabel = myVoteOptions.map((option) => option.label).filter(Boolean).join(', ')
  const commentLength = commentText.length
  const commentTooLong = commentLength > COMMENT_MAX_LENGTH

  const toggleOption = (optionId) => {
    setSelectedOptionIds((prev) => {
      if (!detail?.allowMultiple) return [optionId]
      if (prev.includes(optionId)) return prev.filter((idValue) => idValue !== optionId)
      const max = Number(detail?.maxChoices || options.length || 1)
      return [...prev, optionId].slice(0, max)
    })
  }

  const onVote = async () => {
    if (selectedOptionIds.length === 0) return
    try {
      setVoting(true)
      const res = await api.post(`/vote-board/${id}/votes`, { optionIds: selectedOptionIds })
      setDetail(unwrapData(res))
      try { window?.toast?.show?.('투표에 참여했습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr(getApiErrorMessage(e, '투표 참여에 실패했습니다.'))
    } finally {
      setVoting(false)
    }
  }

  const onComment = async (event) => {
    event.preventDefault()
    const content = commentText.trim()
    if (!content) return
    if (content.length > COMMENT_MAX_LENGTH) {
      setErr(`댓글은 ${COMMENT_MAX_LENGTH.toLocaleString('ko-KR')}자 이내로 입력해 주세요.`)
      return
    }
    try {
      setCommenting(true)
      await api.post(`/vote-board/${id}/comments`, {
        content,
        anonymous: commentAnonymous,

      })
      setCommentText('')
      setCommentAnonymous(true)
      await loadDetail()
    } catch (e) {
      console.error(e)
      setErr(getApiErrorMessage(e, '댓글 등록에 실패했습니다.'))
    } finally {
      setCommenting(false)
    }
  }

  const onDeletePost = async () => {
    if (!detail?.canDelete) return
    if (!window.confirm('이 투표를 삭제할까요?')) return
    try {
      setDeletingPost(true)
      await api.delete(`/vote-board/${id}`)
      navigate('/vote-board')
    } catch (e) {
      console.error(e)
      setErr(getApiErrorMessage(e, '투표 삭제에 실패했습니다.'))
    } finally {
      setDeletingPost(false)
    }
  }

  const onDeleteComment = async (comment) => {
    if (!comment?.id || !comment?.canDelete) return
    if (!window.confirm('이 댓글을 삭제할까요?')) return
    try {
      setDeletingCommentId(comment.id)
      await api.delete(`/vote-board/${id}/comments/${comment.id}`)
      await loadDetail()
    } catch (e) {
      console.error(e)
      setErr(getApiErrorMessage(e, '댓글 삭제에 실패했습니다.'))
    } finally {
      setDeletingCommentId(null)
    }
  }

  const downloadCsv = async () => {
    try {
      const res = await api.get(`/vote-board/${id}/results.csv`, { responseType: 'blob' })
      downloadBlob(res.data, `vote-results-${id}.csv`)
    } catch (e) {
      setErr(getApiErrorMessage(e, '결과 CSV를 내려받지 못했습니다.'))
    }
  }

  const downloadResultImage = async () => {
    if (!resultRef.current) return
    try {
      const canvas = await html2canvas(resultRef.current, { backgroundColor: '#ffffff', scale: 2, useCORS: true })
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
      if (blob) downloadBlob(blob, `vote-results-${id}.png`)
    } catch (e) {
      console.error(e)
      setErr('결과 이미지를 만들지 못했습니다.')
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">투표게시판</h1>
        <div className="flex flex-wrap items-center gap-2">
          {detail?.canEdit && (
            <Link to={`/vote-board/${id}/edit`} className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm hover:bg-slate-50">
              <Pencil className="h-4 w-4" aria-hidden="true" />
              수정
            </Link>
          )}
          {detail?.canDelete && (
            <button type="button" className="inline-flex items-center gap-2 rounded border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60" disabled={deletingPost} onClick={onDeletePost}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              {deletingPost ? '삭제 중...' : '삭제'}
            </button>
          )}
          <Link to="/vote-board" className="rounded border px-3 py-2 text-sm hover:bg-slate-50">목록</Link>
        </div>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {loading ? (
        <div className="text-sm text-slate-600">불러오는 중...</div>
      ) : detail ? (
        <div className="space-y-4">
          <article className="space-y-4 rounded border p-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {detail.notice && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">공지</span>}
                <StatusBadge status={detail.status} />
                {detail.visibilityLabel && <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">{detail.visibilityLabel}</span>}
                <h2 className="text-xl font-semibold text-slate-900">{detail.title}</h2>
              </div>
              <div className="text-xs text-slate-500">
                {formatMenuInstant(detail, 'voteStartAt')} ~ {formatMenuInstant(detail, 'voteEndAt')}
                {detail.createdByName ? ` · 작성자: ${detail.createdByName}` : ''}
                {detail.targetSummary ? ` · ${detail.targetSummary}` : ''}
              </div>
              <div className="flex flex-wrap gap-1">
                {(asArray(detail?.tags)).map((tag) => <span key={tag} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">#{tag}</span>)}
              </div>
            </div>

            <SanitizedRichText html={detail.content} className="system-notice-html" />

            {attachments.length > 0 && (
              <div className="border-t pt-4">
                <div className="mb-2 text-sm font-semibold">첨부 이미지</div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {attachments.map((file) => (
                    <a key={file.id || file.fileId} href={resolveApiAssetUrl(file.fileId)} target="_blank" rel="noreferrer" className="overflow-hidden rounded border bg-white hover:border-slate-300">
                      <div className="aspect-video bg-slate-100">
                        <img src={resolveApiAssetUrl(file.fileId)} alt={file.name || '첨부 이미지'} className="h-full w-full object-cover" loading="lazy" />
                      </div>
                      <div className="px-3 py-2 text-xs text-slate-600">
                        <div className="truncate font-medium text-slate-800">{file.name || file.fileId}</div>
                        <div>{formatFileSize(file.size) || file.contentType || '-'}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </article>

          <div className="rounded border p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <Vote className="h-4 w-4" aria-hidden="true" />
                투표하기
              </div>
              <div className="text-xs text-slate-500">참여 {Number(detail.voteCount || 0).toLocaleString('ko-KR')}명</div>
            </div>
            <div className="space-y-2">
              {options.map((option) => {
                const checked = selectedOptionIds.includes(option.id)
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`w-full rounded border px-4 py-3 text-left ${checked ? 'border-brand-navy bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'} disabled:opacity-60`}
                    onClick={() => toggleOption(option.id)}
                    disabled={!detail.canVote}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${checked ? 'border-brand-navy bg-brand-navy' : 'border-slate-300 bg-white'}`}>
                        {checked && <span className="h-2 w-2 rounded-full bg-white" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium text-slate-900">{option.label}</span>
                        {option.description && <span className="mt-1 block text-sm text-slate-600">{option.description}</span>}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
            {detail.canVote ? (
              <button type="button" className="mt-3 rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" disabled={voting || selectedOptionIds.length === 0} onClick={onVote}>
                {voting ? '참여 중...' : (detail.hasVoted ? '선택 변경' : '투표하기')}
              </button>
            ) : detail.hasVoted ? (
              <div className="mt-3 text-sm text-slate-600">이미 참여했습니다.</div>
            ) : (
              <div className="mt-3 text-sm text-slate-600">현재 참여할 수 없는 투표입니다.</div>
            )}
          </div>

          {detail.canViewResults && detail.results && (
            <div ref={resultRef} className="space-y-4 rounded border bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm">투표 결과</div>
                  <div className="mt-1 text-xs text-slate-500">총 {Number(detail.results.totalVotes || 0).toLocaleString('ko-KR')}명 참여</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={downloadResultImage}>
                    <ImageDown className="h-4 w-4" aria-hidden="true" />
                    이미지
                  </button>
                  {isOperator && (
                    <button type="button" className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={downloadCsv}>
                      <Download className="h-4 w-4" aria-hidden="true" />
                      CSV
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                {(asArray(detail?.results?.options)).map((row) => (
                  <div key={row.optionId} className="space-y-1">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0 truncate font-medium text-slate-800">{row.label}</div>
                      <div className="shrink-0 text-slate-600">{Number(row.voteCount || 0).toLocaleString('ko-KR')}표 · {Number(row.voterPercent || 0).toFixed(1)}%</div>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-brand-navy" style={{ width: `${Math.max(0, Math.min(100, Number(row.voterPercent || 0)))}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              {detail.resultSummary && (
                <div className="rounded border bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-wrap">{detail.resultSummary}</div>
              )}
            </div>
          )}

          {detail.allowComments !== false && (
            <div className="rounded border p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="font-semibold text-sm">댓글</div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" aria-label="댓글 필터: 전체" className={`rounded-full border px-3 py-1 text-xs ${commentFilter === 'ALL' ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200'}`} onClick={() => setCommentFilter('ALL')}>전체</button>
                  {options.map((option) => (
                    <button key={option.id} type="button" aria-label={`댓글 필터: ${option.label}`} className={`rounded-full border px-3 py-1 text-xs ${String(commentFilter) === String(option.id) ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200'}`} onClick={() => setCommentFilter(String(option.id))}>{option.label}</button>
                  ))}
                </div>
              </div>
              <form onSubmit={onComment} className="space-y-3">
                <div className="space-y-1">
                  <textarea className="min-h-[88px] w-full rounded border px-3 py-2" value={commentText} onChange={(e) => setCommentText(e.target.value)} maxLength={COMMENT_MAX_LENGTH} aria-describedby="vote-comment-length" disabled={commenting} required />
                  <div id="vote-comment-length" className={`text-right text-xs ${commentTooLong ? 'text-red-600' : 'text-slate-500'}`}>
                    {commentLength.toLocaleString('ko-KR')} / {COMMENT_MAX_LENGTH.toLocaleString('ko-KR')}
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={commentAnonymous} onChange={(e) => setCommentAnonymous(e.target.checked)} disabled={commenting} />
                      익명으로 표시
                    </label>
                    <div className="rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      내 선택: {myVoteOptionLabel || '투표 참여 전'}
                    </div>
                  </div>
                  <button type="submit" className="rounded border bg-white px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60" disabled={commenting || !commentText.trim() || commentTooLong}>{commenting ? '등록 중...' : '댓글 등록'}</button>
                </div>
              </form>

              <div className="mt-4 divide-y">
                {filteredComments.map((comment) => (
                  <div key={comment.id} className="py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                      <div className="font-medium text-slate-700">
                        {comment.authorDisplayName || '익명'}
                        {comment.selectedOptionLabel ? <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">{comment.selectedOptionLabel}</span> : null}
                        {comment.authorName && comment.anonymous ? <span className="ml-2 text-slate-400">작성자: {comment.authorName}</span> : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <span>{formatMenuInstant(comment, 'createdAt')}</span>
                        {comment.canDelete && (
                          <button type="button" className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60" disabled={deletingCommentId === comment.id} onClick={() => onDeleteComment(comment)}>
                            {deletingCommentId === comment.id ? '삭제 중...' : '삭제'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{comment.content}</div>
                  </div>
                ))}
                {filteredComments.length === 0 && <div className="py-6 text-center text-sm text-slate-500">등록된 댓글이 없습니다.</div>}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded border p-8 text-center text-sm text-slate-500">투표를 찾을 수 없습니다.</div>
      )}
    </section>
  )
}
